import React, { useLayoutEffect } from 'react';
import TextAreaAutosize, { type TextareaAutosizeProps } from 'react-textarea-autosize';
import styled from 'styled-components';
import { sizes } from '../style';
import { useTextareaAutogrowContext } from './textarea-autogrow-context';
import { TextareaAutogrowSizes } from './style';

export type TextAreaAutogrow = TextareaAutosizeProps & { size?: keyof typeof TextareaAutogrowSizes };

const DEFAULT_TEXTAREA_SIZE = 'xl';

const StyledTextAreaAutosize = styled(TextAreaAutosize)<{ $minHeight: string }>`
  width: 100%;
  min-height: ${({ $minHeight }) => $minHeight};
  resize: none;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.5;
  padding: 14px 16px;

  /* Remove default textarea styling since container has it */
  background: transparent;
  border: none;
  box-shadow: none;
  outline: none;

  &:focus {
    outline: none;
  }
`;

const TextareaAutogrowInput: React.FC<TextAreaAutogrow> = ({ value = '', size, onChange, ...textareaProps }) => {
  const { setIsExpanded } = useTextareaAutogrowContext();
  const { height: initialHeight } = sizes[size ?? DEFAULT_TEXTAREA_SIZE];
  const hasExpandedRef = React.useRef(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleHeightChange = (height: number) => {
    const baseHeight = parseInt(initialHeight, 10);

    // Once we expand beyond base height, stay expanded
    if (!hasExpandedRef.current && height > baseHeight) {
      hasExpandedRef.current = true;
      setIsExpanded(true);
    }

    // Reset when empty (height returns to min)
    if (height <= baseHeight && value === '') {
      hasExpandedRef.current = false;
      setIsExpanded(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e as React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>);
  };

  // Reset height when value is cleared
  useLayoutEffect(() => {
    if (value === '' && textareaRef.current) {
      textareaRef.current.style.height = initialHeight;
      hasExpandedRef.current = false;
      setIsExpanded(false);
    }
  }, [value, initialHeight, setIsExpanded]);

  return (
    <StyledTextAreaAutosize
      ref={textareaRef}
      value={value}
      onChange={handleChange}
      onHeightChange={handleHeightChange}
      $minHeight={initialHeight}
      className="textarea-autogrow-input"
      {...textareaProps}
    />
  );
};

export default TextareaAutogrowInput;
